<template>
  <div class="mt-4 mb-4">
    <div>
      <div class="row">
        <div class="col">
          <h2 class="text-center">Add new Data</h2>
        </div>
      </div>

      <div class="row justify-content-center">
        <app-disease-tree-selector
          :branchId.sync="branchId"
          class="col-xl-6 col-lg-6 col-md-5 col-sm-6 col-12 mt-4 mb-4"
          :hasRequiredError="$v.branchId.$error"
        />
      </div>

      <div class="row mt-3">
        <div class="col">
          <h5 class="text-center">New treatment</h5>
        </div>
      </div>

      <b-form @submit.prevent class="form-group row  new-tool" ref="branchForm">
        <div class="col">
          <div class="form-group row justify-content-center">
            <div
              class="col-xl-4 col-lg-4 col-md-5 col-sm-6 col-12 col-form-label text-left new-tool__label"
            >
              Name:
            </div>
            <div
              class="col-xl-4 col-lg-4 col-md-5 col-sm-6 col-12 new-tool__input"
            >
              <input
                :class="{ 'is-invalid': $v.treatmentName.$error }"
                class="form-control"
                type="text"
                placeholder="Treatment name"
                v-model.trim="$v.treatmentName.$model"
              />
              <span class="invalid-feedback" v-if="!$v.treatmentName.required"
                >Name is required</span
              >
              <span class="invalid-feedback" v-if="!$v.treatmentName.minLength">
                Name must be more than
                {{ $v.treatmentName.$params.minLength.min }} letters
              </span>
              <span class="invalid-feedback" v-if="!$v.treatmentName.maxLength">
                Name must be less than
                {{ $v.treatmentName.$params.maxLength.max }} letters
              </span>
            </div>
          </div>

          <div class="row justify-content-center">
            <div class="col-12 mt-3">
              <app-submit-button
                :btnClass="'d-block mx-auto'"
                :caption="'Submit'"
                :submitAction="submitTool"
              />
            </div>
          </div>
        </div>
      </b-form>
    </div>

    <div>
      <div class="row mt-3">
        <div class="col">
          <h5 class="text-center">New attribute</h5>
        </div>
      </div>

      <b-form @submit.prevent class="row" ref="attributeForm">
        <div class="col">
          <div class="form-group row justify-content-center">
            <label
              class="col-xl-4 col-lg-4 col-md-5 col-sm-6 col-12 col-form-label text-left"
              for="group"
              >Group</label
            >

            <app-group-selector
              id="group"
              class="col-xl-4 col-lg-4 col-md-5 col-sm-6 col-12"
              v-model="groupId"
            />
          </div>

          <div class="form-group row justify-content-center">
            <div
              class="col-xl-4 col-lg-4 col-md-5 col-sm-6 col-12 col-form-label text-left"
            >
              Name:
            </div>
            <div class="col-xl-4 col-lg-4 col-md-5 col-sm-6 col-12">
              <input
                class="form-control"
                type="text"
                placeholder="Attribute name"
                v-model.trim="$v.attributeName.$model"
                :class="{ 'is-invalid': $v.attributeName.$error }"
              />
              <span class="invalid-feedback" v-if="!$v.attributeName.required"
                >Name is required</span
              >
              <span class="invalid-feedback" v-if="!$v.attributeName.minLength">
                Name must be more than
                {{ $v.attributeName.$params.minLength.min }} letters
              </span>
              <span class="invalid-feedback" v-if="!$v.attributeName.maxLength">
                Name must be less than
                {{ $v.attributeName.$params.maxLength.max }} letters
              </span>
            </div>
          </div>

          <div class="form-group row justify-content-center">
            <label
              class="col-xl-4 col-lg-4 col-md-5 col-sm-6 col-12 col-form-label text-left"
              for="answerType"
              >Attribute type</label
            >

            <app-attribute-type-selector
              class="col-xl-4 col-lg-4 col-md-5 col-sm-6 col-12"
              id="answerType"
              v-model="attributeType"
            />
          </div>

          <div
            v-if="isMultipleAttributeLines"
            class="row justify-content-center"
          >
            <label
              class="col-xl-4 col-lg-4 col-md-5 col-sm-6 col-12 col-form-label text-left"
              >Attribute values</label
            >

            <app-multiple-attribute-lines
              class="col-xl-4 col-lg-4 col-md-5 col-sm-6 col-12"
              v-model="attributeLines"
            />
          </div>

          <div class="row justify-content-center">
            <div class="col-12 mt-3">
              <app-submit-button
                :btnClass="'d-block mx-auto'"
                :submitAction="submitAttribute"
                :caption="'Submit'"
              />
            </div>
          </div>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import AppSubmitButton from "@/components/AppSubmitButton.vue";
import { maxLength, minLength, required } from "vuelidate/lib/validators";
import { HttpHandler } from "@/http/HttpHandler";
import { NewToolVotingModel } from "@/entities/voting/NewToolVotingModel";
import AppDiseaseTreeSelector from "@/components/AppDiseaseTreeSelector.vue";
import AppAttributeTypeSelector from "@/components/expert/AppAttributeTypeSelector.vue";
import AppMultipleAttributeLines from "@/components/expert/constructor/AppMultipleAttributeLines.vue";
import AppGroupSelector from "@/components/expert/AppGroupSelector.vue";
import { AnswerType } from "@/entities/questionaire/answers/AnswerType";
import constants from "@/entities/constants";
import { NewAttributeVotingModel } from "@/entities/voting/NewAttributeVotingModel";
import store from "@/store/index";

@Component({
  components: {
    AppDiseaseTreeSelector,
    AppSubmitButton,
    AppGroupSelector,
    AppAttributeTypeSelector,
    AppMultipleAttributeLines
  }
})
export default class NewTool extends Vue {
  private branchId: number | null = null;
  private treatmentName: string = "";

  private groupId: number = 0;
  private attributeName: string = "";
  private attributeType: AnswerType = AnswerType.Boolean;
  private attributeLines: { text: string }[] = [];
  private error: boolean = false;

  validations() {
    return {
      branchId: {
        required
      },
      treatmentName: {
        required,
        minLength: minLength(4),
        maxLength: maxLength(70)
      },
      attributeName: {
        required,
        minLength: minLength(4),
        maxLength: maxLength(70)
      }
    };
  }

  get isMultipleAttributeLines(): boolean {
    return this.attributeType !== constants.answerTypes.BooleanAnswer.type;
  }

  @Watch("attributeType")
  onAttributeTypeChanged(newVal: AnswerType) {
    if (newVal === AnswerType.Boolean) {
      this.attributeLines = [];
    }
  }

  async submitAttribute() {
    this.$v.branchId.$touch();
    this.$v.attributeName.$touch();

    if (this.$v.branchId.$invalid || this.$v.attributeName.$invalid) {
      throw new Error("Validation error!");
    }

    const model = new NewAttributeVotingModel(
      this.branchId as number,
      this.groupId,
      this.attributeName,
      this.attributeType,
      this.attributeLines
    );
    const httpHandler = new HttpHandler(store);
    await httpHandler.postNewToolVoting(model).then(() => {
      this.$v.branchId.$reset();
      this.$v.attributeName.$reset();
    });
    await this.$router.push("/voting");
  }

  mounted() {
    this.$events.on("throwError", () => {
      this.error = true;
    });
  }

  beforeDestroy() {
    this.$events.off("throwError");
  }

  async submitTool() {
    this.$v.branchId.$touch();
    this.$v.treatmentName.$touch();

    if (this.$v.branchId.$invalid || this.$v.treatmentName.$invalid) {
      throw new Error("Validation error!");
    }

    const httpHandler = new HttpHandler(store);
    await httpHandler
      .postNewToolVoting(
        new NewToolVotingModel(this.branchId as number, this.treatmentName)
      )
      .then(() => {
        this.$v.branchId.$reset();
        this.$v.treatmentName.$reset();
      });
    await this.$router.push("/voting");
  }
}
</script>

<style scoped></style>
